import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { formatInviteCode, type GroupMemberResponse } from "@qota/shared";
import { Button, Card, Icon, ListRow, Sheet, SkeletonBlock } from "@qota/ui";
import { createInvite, inviteUrl, listInvites, revokeInvite } from "../lib/invites";
import { ApiRequestError } from "../lib/http";
import { queryKeys } from "../lib/query-keys";

/**
 * Schermata 20 — Invita nel gruppo.
 *
 * Due modi di invitare, e il secondo è quello che conta: un invito **mirato a
 * un segnaposto** fa sì che chi entra prenda quel posto e ritrovi le spese già
 * registrate a suo nome, invece di ripartire da zero.
 */
export function InviteSheet({
  groupId,
  members,
  open,
  onClose,
  focusMemberId = null,
}: {
  groupId: string;
  members: GroupMemberResponse[];
  open: boolean;
  onClose: () => void;
  /**
   * Il segnaposto da cui si è arrivati, quando il foglio si apre dal segno
   * accanto a un nome invece che dal pulsante del gruppo.
   *
   * Non crea l'invito da sé: lo mette in cima e lo evidenzia. Un link
   * generato da un tocco che poteva essere solo curiosità è un link che
   * qualcuno dovrà poi revocare.
   */
  focusMemberId?: string | null;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [qrFor, setQrFor] = useState<string | null>(null);
  const [qrImage, setQrImage] = useState<string | null>(null);

  const { data: invites } = useQuery({
    queryKey: queryKeys.invites(groupId),
    queryFn: () => listInvites(groupId),
    enabled: open,
  });

  const creation = useMutation({
    mutationFn: (forMemberId?: string) =>
      createInvite(groupId, { expiresInDays: 7, ...(forMemberId ? { forMemberId } : {}) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.invites(groupId) });
    },
    onError: (caught) =>
      setError(caught instanceof ApiRequestError ? caught.message : "Creazione non riuscita."),
  });

  const revocation = useMutation({
    mutationFn: (inviteId: string) => revokeInvite(groupId, inviteId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.invites(groupId) });
    },
  });

  // Il QR si genera solo quando serve: disegnarne uno per ogni invito in
  // elenco sarebbe lavoro buttato.
  useEffect(() => {
    if (!qrFor) {
      setQrImage(null);
      return;
    }

    let cancelled = false;

    void QRCode.toDataURL(inviteUrl(qrFor), {
      width: 240,
      margin: 1,
      color: { dark: "#0D0D0D", light: "#FFFFFF" },
    }).then((url) => {
      if (!cancelled) setQrImage(url);
    });

    return () => {
      cancelled = true;
    };
  }, [qrFor]);

  async function copy(code: string) {
    await navigator.clipboard.writeText(inviteUrl(code));
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  }

  // Chi ha aperto il foglio da un nome preciso se lo ritrova in cima: con
  // cinque segnaposto, ritrovare quello da cui si è partiti non deve essere un
  // lavoro di ricerca.
  const placeholders = members
    .filter((member) => member.kind === "placeholder")
    .sort((a, b) => Number(b.id === focusMemberId) - Number(a.id === focusMemberId));
  const openInvites = invites?.filter((invite) => invite.usedAt === null) ?? [];

  return (
    <Sheet
      open={open}
      title="Invita nel gruppo"
      subtitle="Chi non ha Qota apre il link, vede il gruppo e crea l'account. Chi ce l'ha entra subito."
      size="tall"
      onClose={() => {
        setQrFor(null);
        onClose();
      }}
      footer={
        <Button variant="secondary" size="lg" full onClick={onClose}>
          Chiudi
        </Button>
      }
    >
      {qrFor ? (
        <div style={{ textAlign: "center" }}>
          {qrImage ? (
            <img
              src={qrImage}
              alt={`QR dell'invito ${formatInviteCode(qrFor)}`}
              style={{ width: 240, height: 240, borderRadius: "var(--radius-md)" }}
            />
          ) : (
            <SkeletonBlock height={200} label="Genero il codice QR" />
          )}

          <div
            className="qota-num"
            style={{ fontSize: 20, fontWeight: 600, letterSpacing: "0.06em", marginTop: 12 }}
          >
            {formatInviteCode(qrFor)}
          </div>

          <Button
            variant="secondary"
            size="md"
            style={{ marginTop: 16 }}
            iconLeft={<Icon name="arrow-left" size={16} />}
            onClick={() => setQrFor(null)}
          >
            Torna agli inviti
          </Button>
        </div>
      ) : (
        <>
          <Button
            variant="accent"
            size="lg"
            full
            disabled={creation.isPending}
            iconLeft={<Icon name="ticket" size={17} />}
            onClick={() => creation.mutate(undefined)}
          >
            {creation.isPending ? "Creo…" : "Crea un link d'invito"}
          </Button>

          {placeholders.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div
                style={{
                  fontSize: "var(--text-label-size)",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  color: "var(--text-secondary)",
                  marginBottom: 6,
                }}
              >
                Invita una persona già segnata
              </div>
              <p
                style={{
                  fontSize: 12,
                  lineHeight: "18px",
                  color: "var(--text-secondary)",
                  margin: "0 0 8px",
                  textWrap: "pretty",
                }}
              >
                Entrando prende il posto del segnaposto e ritrova nel suo saldo le spese già
                registrate a suo nome.
              </p>

              {placeholders.map((member) => (
                <ListRow
                  key={member.id}
                  leading={<Icon name="user-plus" size={17} />}
                  title={member.displayName}
                  chevron={false}
                  style={
                    member.id === focusMemberId
                      ? {
                          background: "var(--surface-sunken)",
                          borderRadius: "var(--radius-sm)",
                          paddingLeft: 10,
                          paddingRight: 10,
                        }
                      : undefined
                  }
                  trailing={
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={creation.isPending}
                      onClick={() => creation.mutate(member.id)}
                    >
                      Invita
                    </Button>
                  }
                />
              ))}
            </div>
          )}

          {openInvites.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div
                style={{
                  fontSize: "var(--text-label-size)",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  color: "var(--text-secondary)",
                  marginBottom: 8,
                }}
              >
                Inviti attivi
              </div>

              {openInvites.map((invite) => (
                <Card
                  key={invite.id}
                  tone="sunken"
                  padding={12}
                  radius="var(--radius-md)"
                  style={{ marginBottom: 8 }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        className="qota-num"
                        style={{ fontSize: 15, fontWeight: 600, letterSpacing: "0.06em" }}
                      >
                        {formatInviteCode(invite.code)}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "var(--text-secondary)",
                          marginTop: 2,
                        }}
                      >
                        {invite.forDisplayName ? `per ${invite.forDisplayName} · ` : ""}
                        scade il{" "}
                        {new Date(invite.expiresAt).toLocaleDateString("it-IT", {
                          day: "numeric",
                          month: "long",
                        })}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <Button variant="secondary" size="sm" onClick={() => void copy(invite.code)}>
                      {copied === invite.code ? "Copiato" : "Copia link"}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setQrFor(invite.code)}>
                      QR
                    </Button>
                    <span style={{ flex: 1 }} />
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={revocation.isPending}
                      onClick={() => revocation.mutate(invite.id)}
                    >
                      Revoca
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {error && (
            <div
              role="alert"
              style={{ fontSize: 12.5, fontWeight: 700, color: "var(--coral-500)", marginTop: 12 }}
            >
              {error}
            </div>
          )}

          <p
            style={{
              fontSize: 11.5,
              lineHeight: "17px",
              color: "var(--text-tertiary)",
              marginTop: 16,
              textWrap: "pretty",
            }}
          >
            Ogni invito vale per una persona sola e scade dopo sette giorni.
          </p>
        </>
      )}
    </Sheet>
  );
}
